import { Close, Save, Cancel, Edit } from "@mui/icons-material";
import {
    Avatar,
    Button,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Paper,
    Typography,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

import thanq from "../api/thanq";

const TipDetails = ({ tip, open, setOpen, onUpdateTip }) => {
    const [editMode, setEditMode] = useState(false);
    const [transactionStatus, setTransactionStatus] = useState("");
    const [payoutStatus, setPayoutStatus] = useState("");
    const [loading, setLoading] = useState(false);
    const [userDetails, setUserDetails] = useState(null);

    const fetchUserDetails = useCallback(async () => {
        if (!tip?.userId) {
            setUserDetails(null);
            return;
        }
        try {
            const res = await thanq.get(`/admin/user/${tip.userId}`);
            setUserDetails(res.data || null);
        } catch (err) {
            console.error("Failed to fetch user details", err);
            enqueueSnackbar("Failed to fetch user details", { variant: "error" });
            setUserDetails(null);
        }
    }, [tip?.userId]);

    useEffect(() => {
        if (tip) {
            setTransactionStatus(tip.transactionStatus || "");
            setPayoutStatus(tip.payoutStatus || "");
            fetchUserDetails();
        }
    }, [tip, fetchUserDetails]);

    const handleSave = async () => {
        setLoading(true);
        try {
            if (onUpdateTip) {
                onUpdateTip(payoutStatus);
            } else {
                enqueueSnackbar("No implementation set for updating tip", { variant: "error" });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Failed to update tip", { variant: "error" });
        } finally {
            handleCancel();
            setLoading(false);
        }
    };

    const handleClose = () => {
        setEditMode(false);
        setOpen(false);
    };

    const handleCancel = () => {
        setTransactionStatus(tip.transactionStatus || "");
        setPayoutStatus(tip.payoutStatus || "");
        setEditMode(false);
    };

    if (!tip) return null;

    return (
        <Drawer anchor="right" open={open} onClose={handleClose}>
            <Box sx={{ width: 400, p: 3 }}>
                <Box
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <Typography variant="h6" gutterBottom>
                        Tip Details
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2} direction={"column"}>
                    <Grid container direction={"row"}>
                        <Typography variant="subtitle2" fontWeight={"bold"}>
                            Tip ID:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            {tip.id}
                        </Typography>
                    </Grid>
                    <Grid container direction={"row"}>
                        <Typography variant="subtitle2" fontWeight={"bold"}>
                            Order ID:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            {tip.orderId}
                        </Typography>
                    </Grid>
                    <Grid container direction={"row"}>
                        <Typography variant="subtitle2" fontWeight={"bold"}>
                            Amount:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            ₹{Number(tip.amount)}/-
                        </Typography>
                    </Grid>
                    <Grid container direction={"row"}>
                        <Typography variant="subtitle2">Created At:</Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            {tip.createdTime
                                ? require("moment")(tip.createdTime).format("lll")
                                : "N/A"}
                        </Typography>
                    </Grid>
                    <Grid container direction={"column"}>
                        <Typography variant="subtitle2" fontWeight={"bold"}>
                            User
                        </Typography>
                        {userDetails ? (
                            <Paper sx={{ p: 1, mb: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Avatar src={userDetails.profileImage} sx={{ mr: 1 }} />
                                    <Box>
                                        <Typography variant="body1">{userDetails.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            @{userDetails.handleId || "N/A"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {userDetails.phoneNumber || ""}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No user details found
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Transaction Status</InputLabel>
                            <Select
                                label="Transaction Status"
                                value={transactionStatus}
                                disabled={true}
                            >
                                <MenuItem value="INITIATED">INITIATED</MenuItem>
                                <MenuItem value="FAILED">FAILED</MenuItem>
                                <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Payout Status</InputLabel>
                            <Select
                                label="Payout Status"
                                value={payoutStatus}
                                disabled={!editMode}
                                onChange={(e) => setPayoutStatus(e.target.value)}
                            >
                                <MenuItem value="INITIATED">INITIATED</MenuItem>
                                <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                                <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                                <MenuItem value="FAILED">FAILED</MenuItem>
                                <MenuItem value="QUEUED">QUEUED</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {editMode ? (
                    <Grid container direction={"row"} justifyContent={"space-between"}>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={loading}
                            sx={{ px: 6 }}
                        >
                            Save
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={handleCancel}
                            disabled={loading}
                            sx={{ px: 6 }}
                        >
                            Cancel
                        </Button>
                    </Grid>
                ) : (
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<Edit />}
                            disabled={true}
                            color="info"
                            onClick={() => setEditMode(true)}
                            sx={{ mb: 1, display: "none" }}
                        >
                            Edit Status
                        </Button>
                    </Grid>
                )}
            </Box>
        </Drawer>
    );
};

export default TipDetails;
